import { useState } from 'react';
import { useT } from '@hlt/web-kit/i18n';
import { Button, CodeInput, Field, RecoveryCodeInput } from '@hlt/web-kit/ui';

/**
 * The second-factor entry of every two-factor challenge (sign-in, confirming a sensitive change, replacing
 * the authenticator): the six code boxes by default, a backup code field on request. A complete code
 * calls `onComplete`, so the surrounding form submits without another press.
 */
export function SecondFactorField({
    value,
    onValueChange,
    onComplete,
    error,
    autoFocus = false,
    label,
    bare = false,
    onRecoveryChange,
}: {
    value: string;
    onValueChange: (value: string) => void;
    onComplete?: (code: string) => void;
    error?: string;
    autoFocus?: boolean;
    label?: string;
    /** Only the six boxes: the label is kept for screen readers and the hint is left out (sign-in step). */
    bare?: boolean;
    onRecoveryChange?: (recovery: boolean) => void;
}) {
    const t = useT();
    const [recovery, setRecovery] = useState(false);
    const plain = bare && !recovery;
    return (
        <div className="flex flex-col gap-2">
            <Field
                label={
                    plain ? (
                        <span className="sr-only">{label ?? t('auth.mfaCode')}</span>
                    ) : recovery ? (
                        t('auth.recoveryCode')
                    ) : (
                        (label ?? t('auth.mfaCode'))
                    )
                }
                hint={plain ? undefined : recovery ? t('auth.recoveryHint') : t('auth.mfaLead')}
                error={error}
            >
                {recovery ? (
                    <RecoveryCodeInput
                        className="min-h-12"
                        autoFocus={autoFocus}
                        value={value}
                        onValueChange={onValueChange}
                    />
                ) : (
                    <CodeInput
                        autoFocus={autoFocus}
                        value={value}
                        onValueChange={onValueChange}
                        onComplete={onComplete}
                    />
                )}
            </Field>
            <Button
                variant="transparent"
                size="sm"
                className="self-start px-0"
                onClick={() => {
                    setRecovery(!recovery);
                    onRecoveryChange?.(!recovery);
                    onValueChange('');
                }}
            >
                {recovery ? t('auth.useAuthenticator') : t('auth.useRecoveryCode')}
            </Button>
        </div>
    );
}
